/* File: frontend/src/pages/ReputationDashboard.js */
/* Version: 16.3 (Final Full Code) */
/* Description: Full dashboard with Heatmap, Strategy Widget, and dedicated 'Execute Fix' modal logic. */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Building2, TrendingUp, AlertTriangle, BarChart3, RefreshCw,
    Plus, MapPin, History, Edit3, Play, Maximize2, Zap,
    Trophy, TrendingDown, Activity, Layers, CheckCircle, X
} from 'lucide-react';
import reputationService from '../services/reputationService';
import AuditRunner from '../components/reputation/AuditRunner';
import ContentFactoryWidget from '../components/dashboard/ContentFactoryWidget';

import ReputationProfileEditor from '../components/reputation/ReputationProfileEditor';
import LocationsBarChart from '../components/dashboard/reputation/LocationsBarChart';
// [Redesign] Feed Removed for Performance & Focus
import OfficeLocationsList from '../components/dashboard/OfficeLocationsList';
import ContentWarfareModal from '../components/reputation/ContentWarfareModal'; // [NEW] Unified Command Center
import { useBranding } from '../context/BrandingContext';
import GrowthWorkspace from '../components/GrowthWorkspace';
import './ReputationDashboard.css';

// Helper to extract City from Address string
const getCityFromAddress = (address) => {
    if (!address) return "New Location";
    const parts = address.split(',');
    if (parts.length >= 2) {
        return parts[1].trim();
    }
    return address.length > 20 ? address.substring(0, 20) + '...' : address;
};

// ============================================================================
// 1. VICTORY PULSE CARD
// ============================================================================
const VictoryPulseCard = ({ winsTotal, winsToday, wins28d, liveAgents, activeLocations }) => (
    <div className="hero-card-battle" style={{
        background: 'white', // [Enterprise] White Background
        cursor: 'default',
        minHeight: '220px',
        height: '100%',
        borderRadius: '24px',
        border: '1px solid #f1f5f9', // [Style] Light Border
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', // [Style] Subtle Shadow
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '24px'
    }}>
        {/* Header Row: Icon + Label */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            {/* Left: Icon Block */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                    width: '56px', height: '56px',
                    borderRadius: '16px',
                    background: '#ecfdf5', // Emerald-50
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#059669' // Emerald-600
                }}>
                    <Trophy size={28} fill="currentColor" />
                </div>
                <div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Victory Pulse
                    </div>
                    <div style={{ fontSize: '3rem', fontWeight: '800', lineHeight: 1, color: '#0f172a', letterSpacing: '-0.02em', marginTop: '4px' }}>
                        {winsTotal}
                    </div>
                </div>
            </div>

            <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>All-Time Wins</div>
            </div>
        </div>

        {/* Secondary Stats */}
        <div style={{ display: 'flex', gap: '32px', marginBottom: 'auto' }}>
            <div>
                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0f172a' }}>+{winsToday}</div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '500' }}>Today</div>
            </div>
            <div>
                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0f172a' }}>{wins28d}</div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '500' }}>Last 28 Days</div>
            </div>
        </div>

        {/* Footer: Operational Stats (Separated) */}
        <div style={{
            marginTop: '20px',
            paddingTop: '16px',
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '0.85rem', fontWeight: '500' }}>
                <Building2 size={16} />
                <span>{activeLocations} Locations</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '0.85rem', fontWeight: '500' }}>
                <Zap size={16} fill={liveAgents > 0 ? "#10b981" : "none"} color={liveAgents > 0 ? "#10b981" : "#64748b"} />
                <span>{liveAgents} Agents</span>
            </div>
        </div>
    </div>
);

// ============================================================================
// 2. HERO BATTLE CARD
// ============================================================================
const HeroBattleCard = ({ activeBattles, onClick }) => (
    <div className="hero-card-battle" onClick={onClick} style={{
        height: '100%',
        minHeight: '220px',
        background: 'white', // [Enterprise] White
        borderRadius: '24px',
        border: '1px solid #f1f5f9',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
    }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                    width: '56px', height: '56px',
                    borderRadius: '16px',
                    background: '#eff6ff', // Blue-50
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#2563eb' // Blue-600
                }}>
                    <Zap size={28} fill="currentColor" />
                </div>
                <div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Agent Battle
                    </div>
                    <div style={{ fontSize: '3rem', fontWeight: '800', lineHeight: 1, color: '#0f172a', letterSpacing: '-0.02em', marginTop: '4px' }}>
                        {activeBattles}
                    </div>
                </div>
            </div>
            {/* Live Badge */}
            <div style={{
                background: '#fffbeb', color: '#b45309', // Amber-50 / Amber-700
                fontSize: '0.75rem', fontWeight: '700',
                padding: '4px 10px', borderRadius: '20px',
                display: 'flex', alignItems: 'center', gap: '6px'
            }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b', animation: 'pulse 2s infinite' }}></span>
                LIVE
            </div>
        </div>

        <div style={{ marginTop: 'auto' }}>
            <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '500', marginBottom: '16px' }}>
                Battles, all time
            </div>

            <div style={{
                borderTop: '1px solid #f1f5f9',
                paddingTop: '16px',
                display: 'flex', justifyContent: 'flex-start'
            }}>
                <button style={{
                    background: 'none', border: 'none', padding: 0,
                    color: '#2563eb', fontWeight: '600', fontSize: '0.9rem',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                }}>
                    Open Full Interface <Maximize2 size={16} />
                </button>
            </div>
        </div>
    </div>
);

// ============================================================================
// 3. REPUTATION HEATMAP
// ============================================================================
const ReputationHeatmap = ({ locations }) => {
    const sorted = [...locations].sort((a, b) => b.win_rate - a.win_rate);

    return (
        <div className="card" style={{
            padding: '24px',
            marginBottom: '25px',
            background: 'white',
            borderRadius: '20px', // [Opinly Style]
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
            border: 'none',
            outline: '1px solid rgba(0,0,0,0.03)'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#1e293b' }}>
                    <MapPin size={20} className="text-blue-500" /> Market Presence Heatmap
                </div>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Performance by Location</div>
            </div>

            <div style={{
                background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #cbd5e1',
                minHeight: '120px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Background Grid */}
                <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)',
                    backgroundSize: '20px 20px', opacity: 0.5
                }}></div>

                {sorted.length > 0 ? sorted.map((loc) => (
                    <div key={loc.id} style={{
                        zIndex: 2,
                        background: 'white',
                        padding: '8px 14px',
                        borderRadius: '30px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        borderLeft: `4px solid ${loc.win_rate > 50 ? '#7fc684' : (loc.win_rate > 20 ? '#d29462' : '#e37275')}`
                    }}>
                        <div style={{
                            width: '8px', height: '8px', borderRadius: '50%',
                            background: loc.win_rate > 50 ? '#7fc684' : (loc.win_rate > 20 ? '#d29462' : '#e37275')
                        }}></div>
                        <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#334155' }}>
                            {getCityFromAddress(loc.address)}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '500' }}>{loc.win_rate}%</div>
                    </div>
                )) : <div style={{ zIndex: 2, color: '#64748b', fontStyle: 'italic' }}>Add locations to generate heatmap</div>}
            </div>
        </div>
    );
};

// ============================================================================
// 4. STRATEGIC WIDGET
// ============================================================================


// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

const ReputationDashboard = () => {
    const location = useLocation();
    const { isFitenko } = useBranding();

    const [stats, setStats] = useState({
        totalProfiles: 0, totalAudits: 0, avgAccuracy: 0, belowThreshold: 0, totalBattles: 0,
        aiVisibility: 0, aiVisibilityVisible: 0, aiVisibilityAnswers: 0,
        aiVisibilityTop3: 0, aiVisibilityTop10: 0, aiVisibilityTop20: 0,
        aiVisibilityVisibleTop3: 0, aiVisibilityVisibleTop20: 0,
        aiVis30d: 0, aiVis30dVisible: 0, aiVis30dAnswers: 0, aiVisByEngine: {},
        totalIssues: 0, criticalIssues: 0, warningIssues: 0,
        globalWinRate: 0, topLocations: [], worstLocations: [], allLocations: [], recentBattles: [],
        winsTotal: 0, winsToday: 0, wins28d: 0, liveAgents: 0, activeLocations: 0,
        brandScore: 0, healthScore: 0,
        articlesCreated: 0, snippetsFixed: 0
    });
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modals state
    const [activeModal, setActiveModal] = useState(null); // 'edit' | 'battle_global' | 'audit' | 'content_warfare' (Unified)
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [initialTab, setInitialTab] = useState('truth');

    // Unified Command Center State
    const [selectedBattle, setSelectedBattle] = useState(null);
    const [modalMode, setModalMode] = useState('attack'); // 'view' | 'attack'

    // Toast Notification State
    const [toast, setToast] = useState(null); // { message: string, type: 'success' | 'error' }

    // === ЭФФЕКТ: Читаем команду на исправление ===
    useEffect(() => {
        if (!loading && profiles.length > 0 && location.state && location.state.action === 'open_fix') {
            const { targetId, type } = location.state;
            const profileToFix = profiles.find(p => p.id === targetId) || profiles[0];

            if (profileToFix) {
                // [FIX] Convert legacy "Fix Issue" trigger to Unified Command Center
                // 🔴 15.09.2026 (quick-260915-did): здесь стояло ВЫДУМАННОЕ место
                //    в выдаче (пятнадцатое) и выдуманный исход «поражение»,
                //    показанные в окне как измеренные. Ранг никто не замерял:
                //    прежний комментарий рядом сам говорил, что он ДОПУЩЕН, —
                //    а на экран число попадало без этой оговорки.
                //    Прежние литералы здесь НЕ ЦИТИРУЮТСЯ: их стережёт
                //    `components/__tests__/vydumannaya-faktura.test.js`.
                //    Теперь оба поля пусты, и окно печатает отсутствие измерения.
                const zadanieNaRabotu = {
                    id: `fix-${profileToFix.id}-${Date.now()}`,
                    keyword: type || "Critical Reputation Gap",
                    location: getCityFromAddress(profileToFix.address),
                    rank: null,
                    outcome: null,
                    profile_id: profileToFix.id,
                    created_at: new Date().toISOString()
                };

                setSelectedBattle(zadanieNaRabotu);
                setModalMode('attack');
                setActiveModal('content_warfare');
                window.history.replaceState({}, document.title);
            }
        }
    }, [loading, profiles, location]);

    // [NEW] Unified Handler for "Execute Fix" / "View Report"
    const openCommandCenter = (profile, mode = 'attack') => {
        // 🔴 Прежде исход битвы ВЫВОДИЛСЯ ИЗ РЕЖИМА ОКНА: нажал «исправить» —
        //    поражение, нажал «посмотреть» — победа. Это не измерение, а
        //    пересказ нажатия, и окно показывало его как замер позиции.
        const zadanieNaRabotu = {
            id: `fix-${profile.id}-${Date.now()}`,
            keyword: "Strategic Gap Analysis",
            location: getCityFromAddress(profile.address),
            rank: null,
            outcome: null,
            profile_id: profile.id,
            created_at: new Date().toISOString()
        };
        setSelectedBattle(zadanieNaRabotu);
        setModalMode(mode);
        setActiveModal('content_warfare');
    };

    // Handler for Chart 'Improve' button
    const handleImproveLocation = (locationStat) => {
        const profile = profiles.find(p => p.id === locationStat.id);
        if (profile) openCommandCenter(profile, 'attack');
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [profilesData, dashboardData] = await Promise.all([
                reputationService.getProfiles(),
                reputationService.getDashboardData()
            ]);

            // --- CALCULATION LOGIC FOR SCORES ---
            let brandScore = dashboardData.brand_authority_score || 0;
            let healthScore = dashboardData.network_health_score || 0;

            if (brandScore === 0 && healthScore === 0 && profilesData.length > 0) {
                const scores = profilesData
                    .map(p => Number(p.latest_accuracy_score) || 0)
                    .filter(s => s > 0);

                if (scores.length > 0) {
                    brandScore = Math.max(...scores);
                    healthScore = (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(0);
                }
            }

            setProfiles(profilesData);
            setStats({
                totalProfiles: profilesData.length,
                // 🔴 260916-wau: складываем ТОЛЬКО измеренные значения. Профиль без
                // замеров (total_audits === null) не входит в сумму слагаемым-нулём:
                // «не мерили» — не то же самое, что измеренный ноль. Заодно уходит
                // молчаливое приведение (acc + null дало бы 0, acc + undefined — NaN).
                totalAudits: profilesData
                    .filter((p) => typeof p.total_audits === 'number')
                    .reduce((acc, p) => acc + p.total_audits, 0),

                brandScore: brandScore,
                healthScore: healthScore,

                belowThreshold: dashboardData.profiles_below_threshold || 0,
                totalBattles: dashboardData.total_battles || 0,

                globalWinRate: dashboardData.global_win_rate || 0,
                aiVisibility: dashboardData.ai_visibility_score || 0,
                aiVisibilityVisible: dashboardData.ai_visibility_visible || 0,
                aiVisibilityAnswers: dashboardData.ai_visibility_answers || 0,
                aiVisibilityTop3: dashboardData.ai_visibility_top3 || 0,
                aiVisibilityTop10: dashboardData.ai_visibility_top10 || 0,
                aiVisibilityTop20: dashboardData.ai_visibility_top20 || 0,
                aiVisibilityVisibleTop3: dashboardData.ai_visibility_visible_top3 || 0,
                aiVisibilityVisibleTop20: dashboardData.ai_visibility_visible_top20 || 0,
                aiVis30d: dashboardData.ai_visibility_30d_score || 0,
                aiVis30dVisible: dashboardData.ai_visibility_30d_visible || 0,
                aiVis30dAnswers: dashboardData.ai_visibility_30d_answers || 0,
                aiVisByEngine: dashboardData.ai_visibility_by_engine || {},
                totalIssues: dashboardData.total_issues || 0,
                criticalIssues: dashboardData.critical_issues || 0,
                warningIssues: dashboardData.warning_issues || 0,
                topLocations: dashboardData.top_locations || [],
                worstLocations: dashboardData.worst_locations || [],
                allLocations: dashboardData.all_locations || [],
                recentBattles: dashboardData.recent_battles || [], // [NEW] Smart Feed

                winsTotal: dashboardData.global_wins_total || 0,
                winsToday: dashboardData.global_wins_today || 0,
                wins28d: dashboardData.global_wins_28d || 0,

                articlesCreated: dashboardData.articles_created || 0,
                snippetsFixed: dashboardData.snippets_fixed || 0,

                liveAgents: dashboardData.active_sessions_now || 0,
                activeLocations: dashboardData.active_workers_today || 0
            });
        } catch (error) {
            console.error("Dashboard Load Error:", error);
            setProfiles([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleSaveProfile = async (profileId, updatedData) => {
        try {
            let savedProfile = updatedData;
            if (profileId && profileId !== 'main' && profileId !== 'hallandale') {
                if (!updatedData?.id) {
                    savedProfile = await reputationService.updateProfile(profileId, updatedData);
                }
            } else if (!updatedData?.id) {
                savedProfile = await reputationService.createProfile(updatedData);
            }
            if (savedProfile) {
                setSelectedProfile(savedProfile);
            }
            await fetchData();
        } catch (e) {
            console.error("Error saving location:", e);
        }
    };

    // --- AUDIT ALL LOCATIONS ---
    const handleAuditAll = async () => {
        if (!profiles || profiles.length === 0) {
            alert("No locations to audit.");
            return;
        }

        const confirmRun = window.confirm(`Are you sure you want to run an audit for ALL ${profiles.length} locations? This might take a few minutes.`);
        if (!confirmRun) return;

        alert(`🚀 Starting massive audit for ${profiles.length} locations. Check the 'Audits' metric shortly.`);

        for (const p of profiles) {
            try {
                const payload = {
                    company_name: p.company_name,
                    company_url: p.company_url || p.website_url,
                    audit_type: "full",
                    llm_model: "gpt-4o"
                };

                if (reputationService.runAuditTask) {
                    await reputationService.runAuditTask(p.id, payload);
                } else {
                    console.warn("reputationService.runAuditTask not found, skipping for now.");
                }

            } catch (e) {
                console.error(`Failed to trigger audit for ${p.company_name}:`, e);
            }
        }
        setTimeout(fetchData, 2000);
    };



    const openEditor = (profile, tab) => {
        setSelectedProfile(profile);
        setInitialTab(tab);
        setActiveModal('edit');
    };

    // ─── ВЕТКА fitenko: Quiet Luxury Growth & Public Trust Center ───
    if (isFitenko) {
        return (
            <>
                {/* 🔴 Пропа `onRunAudit` здесь БОЛЬШЕ НЕТ (снят 17.09.2026).
                    Он уводил главную кнопку шапки в старое окно `AuditRunner`
                    мимо `FullAuditModal` с воротами. Блок `activeModal === 'audit'`
                    ниже оставлен намеренно: у fitenko он остаётся без точки
                    входа — это решение владельца, а не забытый хвост. Ветка
                    рендера для mlg/eln с её собственным блоком `'audit'` не
                    тронута: это мультиарендность, а не дубликат. */}
                <GrowthWorkspace
                    profiles={profiles}
                    stats={stats}
                    onOpenSourceOfTruth={(profile) => {
                        setSelectedProfile(profile || profiles.find(p => p.is_main) || profiles[0]);
                        setInitialTab('truth');
                        setActiveModal('edit');
                    }}
                    onOpenAgentBattle={(profile) => {
                        setSelectedProfile(profile || profiles.find(p => p.is_main) || profiles[0]);
                        setInitialTab('chat');
                        setActiveModal('battle_global');
                    }}
                    onOpenAnalytics={(profile) => {
                        setSelectedProfile(profile || profiles.find(p => p.is_main) || profiles[0]);
                        setInitialTab('analytics');
                        setActiveModal('edit');
                    }}
                    onImproveLocation={handleImproveLocation}
                />

                {/* 1. Source of Truth / Analytics Modal */}
                {activeModal === 'edit' && (
                    <ReputationProfileEditor
                        profile={selectedProfile || profiles.find(p => p.is_main) || profiles[0]}
                        onClose={() => setActiveModal(null)}
                        onSave={handleSaveProfile}
                        viewMode="normal"
                        initialTab={initialTab}
                    />
                )}

                {/* 2. Agent Battle Arena Modal (10 AI Squad) */}
                {activeModal === 'battle_global' && (
                    <ReputationProfileEditor
                        profile={selectedProfile || profiles.find(p => p.is_main) || profiles[0]}
                        onClose={() => setActiveModal(null)}
                        onSave={handleSaveProfile}
                        viewMode="full"
                        initialTab="chat"
                    />
                )}

                {/* 3. Audit Modal for Fitenko */}
                {activeModal === 'audit' && (
                    <div className="modal-overlay">
                        <div className="modal-content" style={{ maxWidth: '600px', height: 'auto' }}>
                            <div className="modal-header">
                                <h2><Play size={20} className="icon-blue" /> Run Audit</h2>
                                <button className="btn-close-modal" onClick={() => setActiveModal(null)}><div /></button>
                            </div>
                            <div style={{ padding: '20px' }}>
                                <AuditRunner
                                    initialCompanyName={selectedProfile?.company_name || 'Fitenko Law PLLC'}
                                    initialUrl={selectedProfile?.website_url || selectedProfile?.company_url || 'https://fitenkolaw.com'}
                                    profileId={selectedProfile?.id}
                                    onAuditComplete={() => { setActiveModal(null); fetchData(); }}
                                />
                                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                                    <button className="btn btn-secondary" onClick={() => setActiveModal(null)}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* 4. Unified Command Center (Warfare Modal) */}
                {activeModal === 'content_warfare' && selectedBattle && (
                    <ContentWarfareModal
                        isOpen={true}
                        battle={selectedBattle}
                        initialMode={modalMode}
                        onClose={() => setActiveModal(null)}
                        onQueueTask={async (battle, tactic, recipe) => {
                            if (reputationService.queueStrategicTask) {
                                await reputationService.queueStrategicTask(battle.id, tactic, recipe);
                            }
                        }}
                    />
                )}
            </>
        );
    }

    return (
        <div className="reputation-dashboard-page" style={{ background: '#f9fafb', minHeight: '100vh', padding: '24px' }}>
            <div className="page-header">
                <h1 className="page-title">Reputation Radar</h1>
                <button className="btn btn-secondary" onClick={fetchData}><RefreshCw size={16} /> Refresh</button>
            </div>

            {/* 1. HERO SECTION (3 Cards) */}
            <div className="hero-section-container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px', marginBottom: '25px', alignItems: 'stretch' }}>
                <HeroBattleCard
                    activeBattles={stats.totalBattles}
                    onClick={() => { setSelectedProfile(null); setInitialTab('chat'); setActiveModal('battle_global'); }}
                />
                <VictoryPulseCard
                    winsTotal={stats.winsTotal}
                    winsToday={stats.winsToday}
                    wins28d={stats.wins28d}
                    liveAgents={stats.liveAgents}
                    activeLocations={stats.activeLocations}
                />
                <ContentFactoryWidget
                    articlesCreated={stats.articlesCreated}
                    snippetsFixed={stats.snippetsFixed}
                />
            </div>

            {/* 2. MAIN DASHBOARD GRID (Responsive Layout) */}
            <div className="dashboard-grid-main" style={{
                display: 'grid',
                // Responsive: 1 column on mobile, 3 columns on desktop
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '24px',
                marginBottom: '25px',
                alignItems: 'start'
            }}>

                {/* COL 1: KPI STACK */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    <div className="card" style={{
                        padding: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        background: 'white',
                        borderRadius: '20px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                        border: 'none',
                        outline: '1px solid rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '0.85rem', marginBottom: '4px' }}
                            title="Share of UNBIASED AI answers (Gemini + DeepSeek, 'who are the best…?') where the firm appears in the top 10. This is the honest AI-search visibility metric — not the prompt-injected Attack number.">
                            <Zap size={16} /> AI Visibility
                        </div>
                        <div style={{ fontSize: '3rem', fontWeight: '800', lineHeight: '1', color: stats.aiVisibility >= 30 ? '#7fc684' : (stats.aiVisibility >= 5 ? '#d29462' : '#e37275') }}>
                            {stats.aiVisibility}%
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                            Top-10 in {stats.aiVisibilityVisible.toLocaleString()}/{stats.aiVisibilityAnswers.toLocaleString()} unbiased AI answers
                        </div>
                        {/* 3-tier breakdown — exposes hidden progress on visibility curve */}
                        <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                            {[
                                { label: 'Top-3',  val: stats.aiVisibilityTop3,  raw: stats.aiVisibilityVisibleTop3,  color: '#7fc684' },
                                { label: 'Top-10', val: stats.aiVisibilityTop10, raw: stats.aiVisibilityVisible,      color: '#d29462' },
                                { label: 'Top-20', val: stats.aiVisibilityTop20, raw: stats.aiVisibilityVisibleTop20, color: '#94a3b8' },
                            ].map(tier => (
                                <div key={tier.label} style={{
                                    flex: 1, padding: '6px 8px', borderRadius: '6px',
                                    background: `${tier.color}14`, border: `1px solid ${tier.color}33`,
                                    textAlign: 'center',
                                }}
                                title={`${tier.raw.toLocaleString()}/${stats.aiVisibilityAnswers.toLocaleString()} audit answers ranked the firm in ${tier.label.toLowerCase()}.`}
                                >
                                    <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: '700', letterSpacing: '0.5px' }}>{tier.label}</div>
                                    <div style={{ fontSize: '0.95rem', fontWeight: '800', color: tier.color }}>{tier.val}%</div>
                                </div>
                            ))}
                        </div>
                        {/* Rolling 30-day window — recent progress vs diluted all-time */}
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '8px' }}
                            title="AI Visibility over the last 30 days only — the all-time number is diluted by old pre-fix battle data.">
                            Last 30d: <strong style={{ color: stats.aiVis30d >= 5 ? '#d29462' : '#94a3b8' }}>{stats.aiVis30d}%</strong>
                            {' '}({stats.aiVis30dVisible.toLocaleString()}/{stats.aiVis30dAnswers.toLocaleString()} answers)
                        </div>
                        {/* Per-engine audit visibility (top-10) */}
                        {Object.keys(stats.aiVisByEngine || {}).length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                                {Object.entries(stats.aiVisByEngine).map(([eng, d]) => (
                                    <span key={eng} style={{ fontSize: '0.62rem', color: '#64748b', background: '#f1f5f9', borderRadius: '4px', padding: '2px 6px' }}
                                        title={`${(d.visible || 0).toLocaleString()}/${(d.answers || 0).toLocaleString()} top-10 audit answers on ${eng}`}>
                                        {eng}: {d.pct}%
                                    </span>
                                ))}
                            </div>
                        )}
                        <div style={{ fontSize: '0.7rem', color: '#cbd5e1', marginTop: '8px', borderTop: '1px solid #f1f5f9', paddingTop: '6px' }}
                            title="Legacy 'attack' win rate — measures whether models comply with a prompt forcing them to name the firm #1. Kept as an injection experiment, not a health metric.">
                            Attack win rate (legacy): {stats.globalWinRate}%
                        </div>
                    </div>

                    <div className="card" style={{
                        padding: '24px',
                        background: 'white',
                        borderRadius: '20px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                        border: 'none',
                        outline: '1px solid rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '0.85rem', marginBottom: '4px' }}>
                            <Building2 size={16} /> Semantic Brand Perception
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#4f46e5' }}>
                                {stats.brandScore > 0 ? stats.brandScore : 'N/A'}
                            </div>
                            <div style={{ fontSize: '1rem', color: '#94a3b8' }}>/ 100</div>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
                            Semantic analysis of how LLMs perceive your brand entity.
                        </div>
                    </div>

                    {/* Health Score Component */}
                    <div className="card" style={{
                        padding: '24px',
                        background: 'white',
                        borderRadius: '20px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                        border: 'none',
                        outline: '1px solid rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1e293b', fontWeight: '700', fontSize: '0.95rem', marginBottom: '15px' }}>
                            <TrendingUp size={18} /> Reputation Health & Strategy
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                            {/* Gauge Visual */}
                            <div style={{
                                width: '80px', height: '80px', borderRadius: '50%',
                                border: '6px solid #e2e8f0',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                position: 'relative'
                            }}>
                                <div style={{
                                    position: 'absolute', top: -6, left: -6, right: -6, bottom: -6, borderRadius: '50%',
                                    background: `conic-gradient(${stats.healthScore >= 70 ? '#7fc684' : (stats.healthScore >= 40 ? '#d29462' : '#e37275')} ${stats.healthScore * 3.6}deg, transparent 0deg)`,
                                    mask: 'radial-gradient(transparent 62%, black 63%)',
                                    WebkitMask: 'radial-gradient(transparent 62%, black 63%)',
                                    opacity: 0.8
                                }}></div>
                                <div style={{ fontSize: '1.8rem', fontWeight: '800', color: stats.healthScore >= 70 ? '#7fc684' : (stats.healthScore >= 40 ? '#d29462' : '#e37275'), lineHeight: 1 }}>{stats.healthScore}</div>
                                <div style={{ fontSize: '0.6rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>AI Health</div>
                            </div>

                            {/* Legend */}
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}
                                     title="Locations with accuracy score < 60 OR attack win rate < 30%. Adds up to critical + warning.">
                                    <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600' }}>TOTAL ISSUES</span>
                                    <span style={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: '700' }}>{stats.totalIssues}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}
                                     title="Locations with accuracy score < 40 OR attack win rate < 10%.">
                                    <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600' }}>CRITICAL</span>
                                    <span style={{ fontSize: '0.9rem', color: '#e37275', fontWeight: '700' }}>{stats.criticalIssues}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}
                                     title="Locations with accuracy 40-60 OR attack win rate 10-30%.">
                                    <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600' }}>WARNING</span>
                                    <span style={{ fontSize: '0.9rem', color: '#d29462', fontWeight: '700' }}>{stats.warningIssues}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '600' }}>STATUS</span>
                                    <span style={{ fontSize: '0.9rem', color: stats.healthScore >= 70 ? '#7fc684' : (stats.healthScore >= 40 ? '#d29462' : '#e37275'), fontWeight: '800' }}>
                                        {stats.healthScore >= 70 ? 'Strong' : (stats.healthScore >= 40 ? 'Fair' : 'Critical')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* COL 2: LISTS (Strengths & Needs Attention) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    <div className="card" style={{
                        padding: '24px',
                        background: 'white',
                        borderRadius: '20px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                        border: 'none',
                        outline: '1px solid rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', color: '#7fc684', fontWeight: '700', borderBottom: '2px solid #ecfdf5', paddingBottom: '10px' }}>
                            <Trophy size={18} /> Top 3 Strongest
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {stats.topLocations && stats.topLocations.length > 0 ? (
                                stats.topLocations.map(t => (
                                    <div key={t.id} style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '12px', marginBottom: '8px',
                                        background: '#f8fafc', borderRadius: '8px', border: '1px solid #f1f5f9'
                                    }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: '600', fontSize: '0.85rem', color: '#334155' }}>{getCityFromAddress(t.address)}</div>
                                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{t.total_battles} Battles</div>
                                        </div>
                                        <div style={{ background: '#dcfce7', color: '#15803d', padding: '4px 8px', borderRadius: '6px', fontWeight: '700', fontSize: '0.75rem' }}>
                                            {t.win_rate}%
                                        </div>
                                    </div>
                                ))
                            ) : <div style={{ color: '#cbd5e1', padding: '10px', fontStyle: 'italic' }}>No data yet</div>}
                        </div>
                    </div>

                    <div className="card" style={{
                        padding: '24px',
                        background: 'white',
                        borderRadius: '20px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                        border: 'none',
                        outline: '1px solid rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', color: '#e37275', fontWeight: '700', borderBottom: '2px solid #fef2f2', paddingBottom: '10px' }}>
                            <TrendingDown size={18} /> Needs Attention
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {stats.worstLocations && stats.worstLocations.length > 0 ? (
                                stats.worstLocations.map(b => (
                                    <div key={b.id} style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '12px', marginBottom: '8px',
                                        background: '#fff1f2', borderRadius: '8px', border: '1px solid #ffe4e6'
                                    }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: '600', fontSize: '0.85rem', color: '#334155' }}>{getCityFromAddress(b.address)}</div>
                                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{b.total_battles} Battles</div>
                                        </div>
                                        <div style={{ background: '#ffe4e6', color: '#e11d48', padding: '4px 8px', borderRadius: '6px', fontWeight: '700', fontSize: '0.75rem' }}>
                                            {b.win_rate}%
                                        </div>
                                    </div>
                                ))
                            ) : <div style={{ color: '#cbd5e1', padding: '10px', fontStyle: 'italic' }}>
                                {stats.allLocations.length === 0
                                    ? 'No active locations yet'
                                    : `Not enough locations to rank: ${stats.allLocations.length} of 3 needed`}
                            </div>}
                        </div>
                    </div>
                </div>

                {/* COL 3: CHARTS */}
                <div>
                    <LocationsBarChart
                        locations={stats.allLocations}
                        onImprove={handleImproveLocation}
                    />
                </div>
            </div>

            {/* 3. COMMAND CENTER (MANAGEMENT ONLY - NO FEED) */}
            <div className="grid grid-cols-1 mt-6" style={{ marginBottom: '25px' }}>
                {/* OFFICE MANAGEMENT (Full Width) */}
                <div style={{
                    background: 'white',
                    borderRadius: '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.025)',
                    overflow: 'hidden',
                    padding: '8px', // Inner padding
                    border: 'none',
                    outline: '1px solid rgba(0,0,0,0.03)'
                }}>
                    <OfficeLocationsList
                        profiles={profiles}
                        loading={loading}
                        onAuditAll={handleAuditAll}
                        onAddLocation={() => { setSelectedProfile({ id: null, company_name: '' }); setInitialTab('truth'); setActiveModal('edit'); }}
                        onSelectProfile={setSelectedProfile}
                        onAuditProfile={() => setActiveModal('audit')}
                        onOpenEditor={openEditor}
                        onImprove={(profile) => openCommandCenter(profile, 'attack')}
                        // [NEW] Logic for Main Location and Delete
                        onSetMain={async (id) => {
                            // 1. Optimistic Update
                            const updatedProfiles = profiles.map(p => ({
                                ...p,
                                isMain: p.id === id ? true : false // Only one can be main
                            }));
                            setProfiles(updatedProfiles);

                            // 2. Persist
                            console.log(`[DASHBOARD] Setting Main Location: ${id}`);
                            try {
                                if (reputationService.setMainLocation) {
                                    await reputationService.setMainLocation(id);
                                    // [FIX] Force refresh to confirm backend persistence
                                    await fetchData();
                                    // [UI] Green Toast
                                    setToast({ message: "Main Location Updated Successfully!", type: "success" });
                                } else {
                                    console.warn("reputationService.setMainLocation not implemented yet.");
                                }
                            } catch (e) {
                                console.error("Failed to set main location:", e);
                                setToast({ message: "Failed to update location.", type: "error" });
                                fetchData(); // Revert
                            }
                        }}
                        onDelete={async (id) => {
                            // [UX] Use Toast instead of window.confirm for cleaner look? No, strictly keep confirm for safety.
                            if (!window.confirm("Are you sure you want to delete this location?")) return;

                            // 1. Optimistic Update
                            const updatedProfiles = profiles.filter(p => p.id !== id);
                            setProfiles(updatedProfiles);

                            // 2. Persist
                            console.log(`[DASHBOARD] Deleting Location: ${id}`);
                            try {
                                await reputationService.deleteProfile(id);
                                setToast({ message: "Location Deleted.", type: "error" }); // Red toast
                            } catch (e) {
                                alert("Failed to delete location: " + e.message);
                                fetchData(); // Revert on error
                            }
                        }}
                    />
                </div>
            </div>

            {/* [NEW] TOAST NOTIFICATION SYSTEM */}
            {toast && (
                <div style={{
                    position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
                    background: toast.type === 'success' ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${toast.type === 'success' ? '#dcfce7' : '#fee2e2'}`,
                    padding: '16px 24px', borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    display: 'flex', alignItems: 'center', gap: '12px',
                    animation: 'slideIn 0.3s ease-out'
                }}>
                    <div style={{
                        color: toast.type === 'success' ? '#16a34a' : '#dc2626',
                        display: 'flex', alignItems: 'center'
                    }}>
                        {toast.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                    </div>
                    <div>
                        <div style={{ fontWeight: '700', color: toast.type === 'success' ? '#15803d' : '#b91c1c', fontSize: '0.95rem' }}>
                            {toast.type === 'success' ? 'Success' : 'Notice'}
                        </div>
                        <div style={{ color: toast.type === 'success' ? '#166534' : '#991b1b', fontSize: '0.85rem' }}>
                            {toast.message}
                        </div>
                    </div>
                    <button
                        onClick={() => setToast(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', marginLeft: '12px' }}
                    >
                        <X size={16} />
                    </button>
                    {/* Auto-dismiss timer */}
                    <div style={{ display: 'none' }}>
                        {setTimeout(() => setToast(null), 4000)}
                    </div>
                </div>
            )}



            {/* --- MODALS --- */}

            {/* 1. EDIT MODAL */}
            {activeModal === 'edit' && (
                <ReputationProfileEditor
                    profile={selectedProfile}
                    onClose={() => setActiveModal(null)}
                    onSave={handleSaveProfile}
                    viewMode="normal"
                    initialTab={initialTab}
                />
            )}

            {/* 2. BATTLE MODAL */}
            {activeModal === 'battle_global' && (
                <ReputationProfileEditor
                    profile={selectedProfile}
                    onClose={() => setActiveModal(null)}
                    onSave={handleSaveProfile}
                    viewMode="full"
                    initialTab="chat"
                />
            )}

            {/* 3. AUDIT MODAL (Only when manually clicked) */}
            {activeModal === 'audit' && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '600px', height: 'auto' }}>
                        <div className="modal-header">
                            <h2><Play size={20} className="icon-blue" /> Run Audit</h2>
                            <button className="btn-close-modal" onClick={() => setActiveModal(null)}><div /></button>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <AuditRunner
                                initialCompanyName={selectedProfile?.company_name}
                                initialUrl={selectedProfile?.website_url || selectedProfile?.company_url}
                                profileId={selectedProfile?.id}
                                onAuditComplete={() => { setActiveModal(null); fetchData(); }}
                            />
                            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                                <button className="btn btn-secondary" onClick={() => setActiveModal(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 4. [NEW] UNIFIED COMMAND CENTER (Replaces legacy Fix Modal) */}
            {activeModal === 'content_warfare' && selectedBattle && (
                <ContentWarfareModal
                    isOpen={true}
                    battle={selectedBattle}
                    initialMode={modalMode} // 'attack' or 'view'
                    onClose={() => setActiveModal(null)}
                    onQueueTask={async (battle, tactic, recipe) => {
                        // Bridge to Service
                        if (reputationService.queueStrategicTask) {
                            await reputationService.queueStrategicTask(battle.id, tactic, recipe);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default ReputationDashboard;